import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from 'styled-components';
import defaultTheme, { darkTheme } from './styled-components-theme';
import './theme.css';

// Define the theme modes
export type ThemeMode = 'light' | 'dark';

// Define the theme context interface
export interface ThemeContextInterface {
  theme: DefaultTheme;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

// Create the theme context
export const ThemeContext = createContext<ThemeContextInterface | undefined>(undefined);

// Define the ThemeProvider props
export interface ThemeProviderProps {
  children: React.ReactNode;
  initialMode?: ThemeMode;
}

/**
 * Apply theme to CSS variables
 * This function applies the theme values to CSS custom properties
 *
 * @param theme The theme object
 * @param mode The theme mode (light or dark)
 */
const applyThemeToCSS = (theme: any, mode: ThemeMode): void => {
  if (typeof document === 'undefined') return;

  try {
    // Apply theme mode class
    if (mode === 'dark') {
      document.documentElement.classList.add('theme-dark');
    } else {
      document.documentElement.classList.remove('theme-dark');
    }

    // Apply colors
    // Primary colors
    if (theme.colors.primary && typeof theme.colors.primary === 'object') {
      Object.entries(theme.colors.primary).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--primary-${key}`, value);
        }
      });
    }

    // Secondary colors
    if (theme.colors.secondary && typeof theme.colors.secondary === 'object') {
      Object.entries(theme.colors.secondary).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--secondary-${key}`, value);
        }
      });
    }

    // Neutral colors
    if (theme.colors.neutral && typeof theme.colors.neutral === 'object') {
      Object.entries(theme.colors.neutral).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--neutral-${key}`, value);
        }
      });
    }

    // Semantic colors
    if (theme.colors.semantic && typeof theme.colors.semantic === 'object') {
      Object.entries(theme.colors.semantic).forEach(([category, values]: [string, any]) => {
        if (values && typeof values === 'object') {
          Object.entries(values).forEach(([key, value]: [string, any]) => {
            if (typeof value === 'string') {
              document.documentElement.style.setProperty(`--${category}-${key}`, value);
            }
          });
        }
      });
    }

    // Theme colors
    if (theme.colors.theme && typeof theme.colors.theme === 'object') {
      Object.entries(theme.colors.theme).forEach(([category, values]: [string, any]) => {
        if (values && typeof values === 'object') {
          Object.entries(values).forEach(([key, value]: [string, any]) => {
            if (typeof value === 'string') {
              document.documentElement.style.setProperty(`--${category}-${key}`, value);
            }
          });
        }
      });
    }

    // Success, Error, Warning, Info colors (Material-UI style)
    ['success', 'error', 'warning', 'info'].forEach(colorType => {
      if (theme.colors[colorType] && typeof theme.colors[colorType] === 'object') {
        Object.entries(theme.colors[colorType]).forEach(([key, value]: [string, any]) => {
          if (typeof value === 'string') {
            document.documentElement.style.setProperty(`--${colorType}-${key}`, value);
          }
        });
      }
    });

    // Background colors
    if (theme.colors.background && typeof theme.colors.background === 'object') {
      Object.entries(theme.colors.background).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--background-${key}`, value);
        }
      });
    }

    // Text colors
    if (theme.colors.text && typeof theme.colors.text === 'object') {
      Object.entries(theme.colors.text).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--text-${key}`, value);
        }
      });
    }

    // Border colors
    if (theme.colors.border && typeof theme.colors.border === 'object') {
      Object.entries(theme.colors.border).forEach(([key, value]: [string, any]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(`--border-${key}`, value);
        }
      });
    }

    // Typography
    if (theme.typography && typeof theme.typography === 'object') {
      // Font family
      if (theme.typography.fontFamily) {
        Object.entries(theme.typography.fontFamily).forEach(([key, value]: [string, any]) => {
          document.documentElement.style.setProperty(`--font-family-${key}`, String(value));
        });
      }

      // Font size
      if (theme.typography.fontSize) {
        Object.entries(theme.typography.fontSize).forEach(([key, value]: [string, any]) => {
          document.documentElement.style.setProperty(`--font-size-${key}`, String(value));
        });
      }

      // Font weight
      if (theme.typography.fontWeight) {
        Object.entries(theme.typography.fontWeight).forEach(([key, value]: [string, any]) => {
          document.documentElement.style.setProperty(`--font-weight-${key}`, String(value));
        });
      }

      // Line height
      if (theme.typography.lineHeight) {
        Object.entries(theme.typography.lineHeight).forEach(([key, value]: [string, any]) => {
          document.documentElement.style.setProperty(`--line-height-${key}`, String(value));
        });
      }
    }

    // Spacing
    if (theme.spacing && typeof theme.spacing === 'object') {
      Object.entries(theme.spacing).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--spacing-${key}`, String(value));
      });
    }

    // Border radius
    if (theme.borderRadius && typeof theme.borderRadius === 'object') {
      Object.entries(theme.borderRadius).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--border-radius-${key}`, String(value));
      });
    }

    // Shadows
    if (theme.shadows && typeof theme.shadows === 'object') {
      Object.entries(theme.shadows).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--shadow-${key}`, String(value));
      });
    }

    // Transitions
    if (theme.transitions && typeof theme.transitions === 'object') {
      Object.entries(theme.transitions).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--transition-${key}`, String(value));
      });
    }

    // Breakpoints
    if (theme.breakpoints && typeof theme.breakpoints === 'object') {
      Object.entries(theme.breakpoints).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--breakpoint-${key}`, String(value));
      });
    }

    // Container
    if (theme.layout && theme.layout.container) {
      Object.entries(theme.layout.container).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--container-${key}`, String(value));
      });
    }

    // Inset
    if (theme.inset) {
      Object.entries(theme.inset).forEach(([key, value]: [string, any]) => {
        document.documentElement.style.setProperty(`--inset-${key}`, String(value));
      });
    }
  } catch (error) {
    console.error('Error applying theme to CSS variables:', error);
  }
};

/**
 * ThemeProvider component
 * This component provides the theme to both styled-components and CSS variables
 *
 * @param props The component props
 * @returns The ThemeProvider component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialMode = 'light',
}) => {
  // State for the current theme mode
  const [mode, setMode] = useState<ThemeMode>(initialMode);

  // Get the theme based on the current mode
  const themeWithMode = useMemo(() => {
    const baseTheme = mode === 'dark' ? darkTheme : defaultTheme;
    return {
      ...baseTheme,
      mode, // Add mode to the theme for DefaultTheme compatibility
    };
  }, [mode]);

  // Toggle the theme mode
  const toggleMode = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  // Apply the theme to CSS variables when the theme changes
  useEffect(() => {
    applyThemeToCSS(themeWithMode, mode);
  }, [themeWithMode, mode]);

  // Create the context value
  const contextValue: ThemeContextInterface = {
    theme: themeWithMode as unknown as DefaultTheme,
    mode,
    setMode,
    toggleMode,
  };

  // Make the theme globally available
  if (typeof window !== 'undefined') {
    window.__STYLED_COMPONENTS_THEME__ = themeWithMode;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={themeWithMode as unknown as DefaultTheme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

/**
 * useTheme hook
 * This hook provides access to the theme context
 *
 * @returns The theme context
 */
export const useTheme = (): ThemeContextInterface => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;